import React, { useCallback, useEffect, useRef, useState } from 'react';
import { primaryButtonClass, secondaryButtonClass } from './formStyles';

/** Keeps the schedule check/save flow pending until the dispatcher chooses. */
export function useScheduleConfirmation() {
  const [message, setMessage] = useState<string | null>(null);
  const pending = useRef<((accepted: boolean) => void) | null>(null);
  const origin = useRef<HTMLElement | null>(null);

  const answer = useCallback((accepted: boolean) => {
    pending.current?.(accepted);
    pending.current = null;
    setMessage(null);
    origin.current?.focus();
  }, []);

  useEffect(() => () => { pending.current?.(false); pending.current = null; }, []);

  const confirm = useCallback((nextMessage: string): Promise<boolean> => {
    origin.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setMessage(nextMessage);
    return new Promise((resolve) => { pending.current = resolve; });
  }, []);

  return { confirm, message, answer };
}

export function ScheduleConfirmation({ message, onAnswer }: { message: string; onAnswer: (accepted: boolean) => void }) {
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancel.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopImmediatePropagation(); onAnswer(false); }
      if (event.key === 'Tab') {
        const buttons = cancel.current?.parentElement?.querySelectorAll<HTMLButtonElement>('button');
        if (!buttons?.length) return;
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', keydown, true);
    return () => window.removeEventListener('keydown', keydown, true);
  }, [onAnswer]);

  const lines = message.split('\n').filter(Boolean);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div role="alertdialog" aria-modal="true" aria-labelledby="schedule-confirm-title" aria-describedby="schedule-confirm-detail"
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
        <h2 id="schedule-confirm-title" className="text-lg font-semibold text-gray-900">Review schedule change</h2>
        <div id="schedule-confirm-detail" className="mt-3 space-y-2 text-sm text-gray-700">
          {lines.map((line, index) => <p key={index} className={line.startsWith('- ') ? 'rounded bg-amber-50 px-3 py-2 text-amber-900' : ''}>{line}</p>)}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button ref={cancel} type="button" className={secondaryButtonClass} onClick={() => onAnswer(false)}>Cancel</button>
          <button type="button" className={primaryButtonClass} onClick={() => onAnswer(true)}>Save anyway</button>
        </div>
      </div>
    </div>
  );
}
